import { isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';

import {
  createEsignatureIntegration,
  deleteEsignatureIntegration,
  getEsignatureIntegrationLoginUrl,
  updateEsignatureIntegration,
} from '~/api/integrations';
import { getEsignatureIntegrations } from '~/common/constants/integrations';
import logos from '~/components/Shared/ProviderLogo/logos';
import { Button } from '~/eds/src/components/Button/Button';
import { useAsync } from '~/hooks/useAsync';
import { useIntl } from 'react-intl';
import Alert from '~/ui/components/Alert/Alert';
import Box from '~/ui/components/Box/Box';
import Card from '~/ui/components/Card/index';
import Divider from '~/ui/components/Divider/Divider';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import InputLabel from '~/ui/components/InputLabel/InputLabel';
import RadioGroup from '~/ui/components/RadioGroup/RadioGroup';

import IntegrationDetailCard from '../../IntegrationDetailCard';
import ContactIntegrationMessage from '../ContactIntegrationMessage';
import StatusAndActions from './StatusAndActions';
import SwitchLabel from './SwitchLabel';

function ESignatureIntegrationCard({
  activeEsignatureIntegrationCount,
  esignatureIntegration,
  isRefetching,
  onRefetch,
}) {
  const intl = useIntl();
  const [draft, setDraft] = useState(esignatureIntegration);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const { error, id, type, disabled } = esignatureIntegration;
  const esignatureIntegrations = getEsignatureIntegrations();
  const { name, provider } = esignatureIntegrations[type];
  const description = intl.formatMessage(
    {
      id: 'ESignatureIntegrationCard.description',
      defaultMessage:
        "Use {name}'s e-signature tool for your workflows. Contracts created or processed through our workflow tool will be routed for signature using {name}.",
      description:
        'E-signature integration description. Example: name = "Document Name"',
    },
    { name },
  );

  const useCompanyAccountOptions = [
    {
      label: intl.formatMessage({
        id: 'ESignatureIntegrationCard.usePersonalAccount',
        defaultMessage: 'Individually (needs paid plan)',
        description: 'Label for using personal account option',
      }),
      value: false,
    },
    {
      label: intl.formatMessage({
        id: 'ESignatureIntegrationCard.useCompanyAccount',
        defaultMessage: 'Using the provided company account (needs paid plan)',
        description: 'Label for using company account option',
      }),
      value: true,
    },
  ];

  const logoSrc = logos[provider];

  const isUndefined = !disabled && !id;
  const hasOtherActiveIntegration =
    activeEsignatureIntegrationCount > 0 && isUndefined;

  useEffect(() => {
    setDraft(esignatureIntegration);
  }, [esignatureIntegration]);

  const { executor: fetchIntegrationLoginUrl } = useAsync(
    getEsignatureIntegrationLoginUrl,
    {
      integrationId: draft.id,
      integrationUrl: window.location.href,
    },
    {
      errorToastMessage: intl.formatMessage({
        id: 'ESignatureIntegrationCard.fetchLoginUrlError',
        defaultMessage:
          'Unable to fetch esignature integration login url, please try again',
        description: 'Error message for fetch login URL failure',
      }),
      successHandler: ({ redirectUrl }) => (window.location.href = redirectUrl),
    },
  );

  const { executor: executeDeleteIntegration, isLoading: isDeleting } =
    useAsync(deleteEsignatureIntegration, esignatureIntegration, {
      errorToastMessage: intl.formatMessage({
        id: 'ESignatureIntegrationCard.deleteError',
        defaultMessage:
          'Unable to delete esignature integration, please try again',
        description: 'Error message for delete integration failure',
      }),
      successHandler: onRefetch,
    });

  const { executor: executeCreateIntegration, isLoading: isCreating } =
    useAsync(createEsignatureIntegration, draft, {
      errorToastMessage: intl.formatMessage({
        id: 'ESignatureIntegrationCard.createError',
        defaultMessage:
          'Unable to create esignature integration, please try again',
        description: 'Error message for create integration failure',
      }),
      successHandler: (res) => {
        setDraft(res);
        if (!res.isUserLoggedIn) {
          fetchIntegrationLoginUrl();
        } else {
          onRefetch(res);
        }
      },
    });

  const { executor: executeUpdateIntegration, isLoading: isUpdating } =
    useAsync(updateEsignatureIntegration, draft, {
      errorToastMessage: intl.formatMessage({
        id: 'ESignatureIntegrationCard.updateError',
        defaultMessage:
          'Unable to update esignature integration, please try again',
        description: 'Error message for update integration failure',
      }),
      successHandler: (res) => {
        setDraft(res);
        if (!res.isUserLoggedIn) {
          fetchIntegrationLoginUrl();
        } else {
          onRefetch(res);
        }
      },
    });

  const isUnchanged = isEqual(draft, esignatureIntegration);
  const isLoading = isRefetching || isCreating || isDeleting || isUpdating;
  const hasError = !!error;

  function handleCancel() {
    setDraft(esignatureIntegration);
    setIsCollapsed(true);
  }

  function handleConnect() {
    setIsCollapsed(false);
  }

  function handleSave() {
    const executeSave = id
      ? executeUpdateIntegration
      : executeCreateIntegration;
    executeSave();
  }

  function handleUpdateDraft(key) {
    return (updatedValue) => {
      setDraft((previousDraft) => ({
        ...previousDraft,
        [key]: updatedValue,
      }));
    };
  }

  const headerRightContent = (
    <StatusAndActions
      disabled={disabled}
      esignatureIntegration={esignatureIntegration}
      isActive={!isCollapsed}
      isLoading={isLoading}
      isPending={!isUnchanged}
      onConnect={handleConnect}
      onDelete={executeDeleteIntegration}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
    />
  );

  const footer = (
    <Card.Footer
      actionButtons={[
        <Button
          disabled={isUnchanged || isLoading}
          text={intl.formatMessage({
            id: 'ESignatureIntegrationCard.cancel',
            defaultMessage: 'Cancel',
            description: 'Cancel button text',
          })}
          variant="tertiary"
          onClick={handleCancel}
        />,
        <Button
          disabled={isUnchanged && !hasError}
          isLoading={isLoading}
          text={intl.formatMessage({
            id: 'ESignatureIntegrationCard.save',
            defaultMessage: 'Save',
            description: 'Save button text',
          })}
          onClick={handleSave}
        />,
      ]}
    />
  );

  return (
    <IntegrationDetailCard
      isCollapsed={isCollapsed}
      footer={footer}
      headerRightContent={headerRightContent}
      logoSrc={logoSrc}
      title={name}
      onToggleCollapse={setIsCollapsed}
    >
      <FlexLayout
        alignItems="stretch"
        as="form"
        flexDirection="column"
        space={6}
      >
        {disabled && (
          <>
            <ContactIntegrationMessage
              description={description}
              enableIcon={true}
            />
            <Divider />
          </>
        )}
        {!hasOtherActiveIntegration && (
          <Box sx={{ alignSelf: 'flex-start' }}>
            <Alert enableIcon variant="warning">
              {intl.formatMessage({
                id: 'ESignatureIntegrationCard.permissionsWarning',
                defaultMessage:
                  'Please make sure to set correct permissions before generating the key (Evisort can modify envelopes for all users).',
                description: 'Warning about setting correct permissions',
              })}
            </Alert>
          </Box>
        )}
        {hasOtherActiveIntegration && (
          <>
            <ContactIntegrationMessage
              description={description}
              disabledFeatureDescription={intl.formatMessage(
                {
                  id: 'ESignatureIntegrationCard.disabledFeatureDescription',
                  defaultMessage:
                    'This integration is not currently available. To start using {name}, remove the currently active e-signature provider first.',
                  description:
                    'Description for disabled feature. Example: name = "Document Name"',
                },
                {
                  name,
                },
              )}
              enableIcon={true}
            />
            <Divider />
          </>
        )}
        <FlexLayout
          disabled={disabled || hasOtherActiveIntegration}
          flexDirection="column"
          space={6}
        >
          <InputLabel
            isRequired
            id="use-company-account"
            label={intl.formatMessage({
              id: 'ESignatureIntegrationCard.authenticationLabel',
              defaultMessage:
                'When collecting signatures, Evisort users will authenticate:',
              description: 'Label for authentication options',
            })}
          >
            <RadioGroup
              options={useCompanyAccountOptions}
              value={draft.shouldUseCompanyAccount}
              onChange={handleUpdateDraft('shouldUseCompanyAccount')}
            />
          </InputLabel>
          <Divider />
          <SwitchLabel
            isRequired
            disabled={activeEsignatureIntegrationCount < 2}
            id="is-default"
            label={intl.formatMessage({
              id: 'ESignatureIntegrationCard.defaultProviderLabel',
              defaultMessage: 'Default e-signature provider',
              description: 'Label for default provider setting',
            })}
            message={intl.formatMessage({
              id: 'ESignatureIntegrationCard.defaultProviderMessage',
              defaultMessage:
                'All documents will be signed using this provider',
              description: 'Message explaining default provider behavior',
            })}
            value={draft.isDefault}
            onChange={handleUpdateDraft('isDefault')}
            width="fullWidth"
          />
        </FlexLayout>
      </FlexLayout>
    </IntegrationDetailCard>
  );
}

export default ESignatureIntegrationCard;
